import "./lesson-video.less"
import { Play, Clock, Film } from "lucide-react"
import { useState, useRef, useCallback } from "react"

interface LessonVideoProps {
  title: string
  videoUrl?: string
  thumbnailUrl?: string
  duration?: string
}

/** Check if a video URL is a valid playable URL (not a placeholder) */
function isPlayableUrl(url?: string): boolean {
  if (!url) return false
  // Filter out obviously invalid/placeholder URLs
  if (url.includes("example.com") || url.includes("placeholder")) return false
  try {
    const parsed = new URL(url)
    return parsed.protocol === "http:" || parsed.protocol === "https:"
  } catch {
    return false
  }
}

export function LessonVideo({
  title,
  videoUrl,
  thumbnailUrl,
  duration,
}: LessonVideoProps) {
  const [playing, setPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const playable = isPlayableUrl(videoUrl)

  const handlePlay = useCallback(() => {
    if (!playable) return
    setPlaying(true)
    // Small delay to let video element render, then play
    setTimeout(() => videoRef.current?.play(), 100)
  }, [playable])

  // If playing, show the native <video> player
  if (playing && playable) {
    return (
      <div className="lesson-video lesson-video--playing">
        <video
          ref={videoRef}
          src={videoUrl}
          controls
          autoPlay
          className="lesson-video__player"
        >
          <track kind="captions" />
        </video>

      </div>
    )
  }

  // If the video URL is a placeholder/invalid, show a demo card
  if (!playable) {
    return (
      <div className="lesson-video">
        <div className="lesson-video__placeholder" />
        <div className="lesson-video__overlay" />
        <div className="lesson-video__play-area">
          <div className="lesson-video__demo-content">
            <Film className="lesson-video__demo-icon" />
            <p className="lesson-video__demo-text">课程视频即将上线</p>
            <p className="lesson-video__demo-subtext">课程视频正在制作中，敬请期待</p>
          </div>
        </div>

      </div>
    )
  }

  // Poster / thumbnail state
  return (
    <div className="lesson-video">
      {/* Thumbnail / video background */}
      {thumbnailUrl ? (
        <img
          src={thumbnailUrl}
          alt={title}
          className="lesson-video__thumbnail"
        />
      ) : (
        <div className="lesson-video__placeholder" />
      )}

      {/* Dark overlay */}
      <div className="lesson-video__overlay" />

      {/* Center play button */}
      <button
        type="button"
        onClick={handlePlay}
        className="lesson-video__play-area"
      >
        <div className="lesson-video__play-btn">
          <Play className="lesson-video__play-icon" fill="white" />
        </div>
      </button>

      {/* Bottom gradient overlay with title + duration */}
      <div className="lesson-video__bottom-bar">
        <div className="lesson-video__bottom-content">
          <h3 className="lesson-video__title">{title}</h3>
          {duration && (
            <span className="lesson-video__duration">
              <Clock className="h-3.5 w-3.5" />
              {duration}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
